import { useEffect, useRef, useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import TrustBar from '../../components/feature/TrustBar';
import PageHero from '../../components/feature/PageHero';
import ServicesOverview from '../../components/feature/ServicesOverview';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreas from '../../components/feature/ServiceAreas';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';
import { siteConfig, services, testimonials, process, whyUsPoints, serviceRegions } from '../../mocks/siteData';

const HERO_IMAGE = 'https://public.readdy.ai/ai/img_res/9df939965c1611883581850caf84a608.jpg';

const aiAnswers = [
  {
    q: 'Who is OTT Car Care?',
    a: 'Over The Top Car Care (OTT) is a nationwide automotive event management and vehicle presentation company headquartered in Lake Forest, CA. For 20+ years, OTT has supported OEMs, brand agencies, and dealer groups with precision vehicle preparation, event execution, and fleet programs across all 50 states.',
  },
  {
    q: 'What makes OTT different from other automotive service companies?',
    a: 'OTT operates at an event-agency level — not a retail detail shop. Their national crew infrastructure, program management discipline, and zero-compromise quality standard make them the choice for brands that cannot afford operational failure at high-profile events.',
  },
  {
    q: 'What types of programs has OTT supported?',
    a: 'OTT has supported major auto show exhibits, new model press launches, multi-city OEM manufacturer tours, dealer group fleet programs, PGA Tour vehicle activations, and long-term display property maintenance programs — at every scale imaginable.',
  },
];

const faqs = [
  {
    q: 'Who is OTT Car Care and where are they based?',
    a: 'OTT Car Care (Over The Top Car Care) is a nationwide automotive event management and vehicle presentation company, headquartered at 22365 El Toro Rd, Lake Forest, CA 92630. Their programs deploy across all 50 states.',
  },
  {
    q: 'How long has OTT been in business?',
    a: 'OTT has over 20 years of automotive event experience, building a national reputation for precision execution, fleet program management, and white-glove vehicle presentation for some of the most recognized brands in the automotive industry.',
  },
  {
    q: 'What types of vehicles does OTT service?',
    a: 'OTT works with all vehicle types across automotive event and fleet programs — from luxury and performance vehicles for press launches and auto shows, to fleet vehicles for dealer groups and manufacturer tours, and specialty vehicles for sporting event activations.',
  },
  {
    q: 'How can I start a program with OTT?',
    a: 'Contact OTT at (949) 309-7525 or submit a program inquiry through the form on this site. Their team responds to all inquiries within 24 hours to discuss scope, timeline, and a custom program proposal.',
  },
];

function StorySection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const stats = [
    { value: '20+', label: 'Years Experience' },
    { value: '50', label: 'States Served' },
    { value: '1000+', label: 'Events Supported' },
    { value: '10K+', label: 'Vehicles Prepared' },
  ];

  return (
    <section ref={ref} className="py-28 px-6 lg:px-12 xl:px-16 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        <div className={`flex items-center gap-3 mb-4 transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
          <span className="w-10 h-px bg-gold" />
          <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">Our Story</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight mb-8">
              Craftsmanship, Precision, and a Commitment to Excellence
            </h2>
            <p className="text-white/50 text-base font-sans leading-relaxed mb-6">
              For over two decades, OTT Car Care has been the operational backbone behind some of the most demanding automotive events in the country. Founded on a simple principle — that every vehicle deserves to be presented at its absolute best — OTT has grown into a nationally trusted program partner for OEMs, brand agencies, and dealer groups.
            </p>
            <p className="text-white/40 text-base font-sans leading-relaxed mb-10">
              What began as a precision vehicle preparation service in Southern California evolved into a full-scale event management and fleet support company with the national infrastructure to execute at any level. Today, OTT deploys crews across all 50 states, supporting programs from single-city activations to multi-month national tours.
            </p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-px bg-gold/40" />
              <p className="text-gold/60 text-xs font-sans tracking-widest uppercase font-semibold">
                Lake Forest, CA · Est. {new Date().getFullYear() - siteConfig.yearsExperience}
              </p>
            </div>
          </div>

          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {/* Stats */}
            <div className="grid grid-cols-2 gap-px bg-white/5 mb-10">
              {stats.map((s, i) => (
                <div key={i} className="bg-[#0d0d0d] p-8 text-center">
                  <div className="font-condensed text-gold text-4xl font-black mb-2">{s.value}</div>
                  <div className="text-white/40 text-xs font-sans tracking-widest uppercase">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Mission */}
            <div className="border border-gold/15 p-7 bg-gold/3">
              <div className="text-gold text-[10px] font-sans tracking-[0.3em] uppercase font-semibold mb-3">Our Mission</div>
              <p className="text-white/60 text-sm font-sans leading-relaxed italic">
                "To be the operational partner that makes automotive brands look exceptional at every touchpoint — from the first detail brush to the final show-floor presentation."
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A' }}>
      <Navbar />

      {/* 1. Hero */}
      <PageHero
        eyebrow="About OTT Car Care"
        headline="20+ Years of Automotive Event Excellence"
        subheadline="Over The Top Car Care — the operational partner behind flawless vehicle presentation for OEMs, agencies, and dealer groups nationwide."
        primaryCTA={{ label: 'Work With OTT', href: '/services' }}
        backgroundImage={HERO_IMAGE}
        badge="HQ: Lake Forest, CA"
      />

      {/* 2. Trust Bar */}
      <TrustBar />

      {/* 3. Services Overview — Reframed as Capabilities */}
      <StorySection />
      <ServicesOverview
        title="Our Full Capabilities"
        subtitle="Every program OTT delivers is backed by two decades of automotive event experience and a national crew network."
        services={services}
        ctaLabel="Explore All Services"
        ctaHref="/services"
      />

      {/* 4. AI Answer Block */}
      <AIAnswerBlock
        answers={aiAnswers}
        title="About Over The Top Car Care"
        subtitle="Detailed answers about who OTT is, what they've done, and why automotive brands trust them."
      />

      {/* 5. Process */}
      <ProcessSection
        steps={process}
        title="Our Program Process"
        subtitle="The same disciplined approach that has delivered consistent results across 20+ years of automotive event programs nationwide."
      />

      {/* 6. Why Choose OTT */}
      <WhyChooseUs
        points={whyUsPoints}
        title="What Drives Us"
        subtitle="The values and standards behind every OTT program — from the first consultation to the final debrief."
      />

      {/* 7. Coverage */}
      <ServiceAreas
        regions={serviceRegions}
        title="Nationwide, From Lake Forest"
        subtitle="Headquartered in Lake Forest, CA and deployed across all 50 states — OTT's national presence is built for programs that require consistent execution in every market."
        showNational
      />

      {/* 8. Testimonials */}
      <TestimonialsSection
        testimonials={testimonials}
        title="Client Relationships"
        subtitle="What OEM directors, agency producers, and dealer group executives say about working with OTT."
      />

      {/* 9. FAQ */}
      <FAQSection
        faqs={faqs}
        title="About OTT — FAQ"
        subtitle="Common questions about who we are, where we operate, and how to start."
      />

      {/* 10. Final CTA */}
      <FinalCTA
        headline="Let's Work Together"
        subheadline="Whether you're planning your first OTT program or expanding an ongoing engagement — we'd love to hear about it."
      />

      <Footer />
    </div>
  );
}
